import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { DrawerRouteLevel } from "shared-ui/src/components/detail-drawer/drawer-stack";
import { useInvoiceDrawerReady } from "./InvoiceDrawerReadyContext";
import {
  APPROVAL_DRAWER_SEGMENT,
  INTEGRATION_TRANSFER_DRAWER_SEGMENT,
  INVOICE_DRAWER_SEGMENT,
  ORDER_DRAWER_SEGMENT,
  TRACKING_DRAWER_SEGMENT,
} from "./orderDrawerSegments";

interface DrawerBaseReadinessValue {
  readyPaths: ReadonlySet<string>;
  register: (path: string) => () => void;
}

const DrawerBaseReadinessContext =
  createContext<DrawerBaseReadinessValue | null>(null);

export function DrawerBaseReadinessProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [registrations, setRegistrations] = useState<
    ReadonlyMap<string, number>
  >(() => new Map());
  const register = useCallback((path: string) => {
    setRegistrations((previous) => {
      const next = new Map(previous);
      next.set(path, (next.get(path) ?? 0) + 1);
      return next;
    });
    return () =>
      setRegistrations((previous) => {
        const next = new Map(previous);
        const count = next.get(path) ?? 0;
        if (count <= 1) next.delete(path);
        else next.set(path, count - 1);
        return next;
      });
  }, []);
  const value = useMemo(
    () => ({ readyPaths: new Set(registrations.keys()), register }),
    [registrations, register],
  );
  return (
    <DrawerBaseReadinessContext.Provider value={value}>
      {children}
    </DrawerBaseReadinessContext.Provider>
  );
}

/** Registers only after the owning modal has mounted. */
export function ReadyDrawerBase({
  path,
  children,
}: {
  path: string | null;
  children: ReactNode;
}) {
  const register = useContext(DrawerBaseReadinessContext)?.register;
  useEffect(() => {
    if (path && register) return register(path);
  }, [path, register]);
  return children;
}

/** Wait for ancestor modals that can be replaced during loading before mounting a child. */
export function useDrawerAncestorsReady(): (
  ancestors: readonly DrawerRouteLevel[],
) => boolean {
  const base = useContext(DrawerBaseReadinessContext);
  const invoice = useInvoiceDrawerReady();
  return useCallback(
    (ancestors: readonly DrawerRouteLevel[]) =>
      ancestors.every((ancestor) => {
        if (
          ancestor.segment === ORDER_DRAWER_SEGMENT ||
          ancestor.segment === APPROVAL_DRAWER_SEGMENT ||
          ancestor.segment === TRACKING_DRAWER_SEGMENT ||
          ancestor.segment === INTEGRATION_TRANSFER_DRAWER_SEGMENT
        ) {
          return !base || base.readyPaths.has(ancestor.path);
        }
        if (ancestor.segment === INVOICE_DRAWER_SEGMENT) {
          return !invoice || invoice.readyPath === ancestor.path;
        }
        return true;
      }),
    [base, invoice],
  );
}
